<script lang="ts">
import { COMPLAINT_STATUS, COMPLAINT_STATUS_OPTIONS, FLIGHT_STATUS, FLIGHT_STATUS_OPTIONS, REQUEST_STATUS } from '@/app/const';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import { FlightInfo } from '@/interface/FlightInfo.interface';
import { useRouter } from 'vue-router';
import { ref } from 'vue';
import { flightDateTimeStrToAppFormat, formateDate } from '@/app/common/dateFormate';
import ConfirmDialog from '@/app/common/components/ConfirmDialog.vue';
import PreComplaint from '../complaint/preComplaint.vue';
import CommonService from '@/services/commonService';
import { useI18n } from 'vue-i18n';
import { complaintService } from '@/services/serviceProvider';

export default {
    components: {
        PreComplaint,
        ConfirmDialog,
    },
    setup() {
        const { t } = useI18n();
        const router = useRouter();
        const preComplaint = ref();
        const commonSv = new CommonService(t);

        return {
            router, preComplaint, commonSv
        };
    },
    props: {
        modelValue: {
            type: Object,
            default: undefined
        },
        item: {
            type: Object,
            default: undefined
        },
        showFlightDetail: {
            type: Boolean,
            default: true
        },
        showInputAirport: {
            type: Boolean,
            default: false
        },
        viewMode: {
            type: Boolean,
            default: false
        },
        selectedCard: {
            type: Boolean,
            default: false
        }
    },
    emits: [
        'update:modelValue',
    ],
    data() {
        return {
            flightItem: this.item as FlightInfo,
            flightStatusOptions: FLIGHT_STATUS_OPTIONS,
            complainStatusOptions: COMPLAINT_STATUS_OPTIONS,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            },
        }
    },
    watch: {
        item(newVal, oldVal) {
            if (newVal) {
                this.flightItem = newVal;
            }
        },
    },
    computed: {
        model: {
            get() {
                return this.modelValue || {} as ComplaintInfo;
            },
            set(newValue: ComplaintInfo) {
                this.$emit('update:modelValue', newValue);
            }
        },
        isViewDoc() {
            return !this.showFlightDetail && this.viewMode;
        },
    },
    async mounted() {
    },
    methods: {
        // allowComplaint(flight: FlightInfo) {
        //     return flight.complaintStatus === FLIGHT_STATUS.DELAYED
        //         || flight.flightStatus === FLIGHT_STATUS.CANCELLED
        // },
        canComplaint(flight: FlightInfo) {
            return flight.complaintStatus === COMPLAINT_STATUS.CAN_COMPLAINT
        },
        isDelayed(flightStatus: string) {
            return flightStatus === FLIGHT_STATUS.DELAYED
                || flightStatus === FLIGHT_STATUS.DELAYED30
        },
        displaySubTopic(display: boolean) {
            if (this.preComplaint) {
                this.preComplaint.displaySubTopicDialog(display);
            }
        },
        dateString(date: any) {
            return complaintService.dateTimeString(date);
        },
        appDateTimeString(date: any) {
            return flightDateTimeStrToAppFormat(date);
        },
        complaintStatusLabel() {
            return this.complainStatusOptions.find(o => o.value === this.flightItem.complaintStatus)?.label;
        },
        flightStatusLabel() {
            return this.flightStatusOptions.find(o => o.value === this.flightItem.flightStatus)?.label
        },
    },
};
</script>
<template>
    <div v-if="selectedCard" class="flight-selected-tag">
        <div class="tag-content">
            {{ $t('t-complaint-flight-selected') }}
        </div>
    </div>
    <v-card v-if="flightItem" class="flight-card px-0 py-0" :class="isViewDoc ? 'request mx-6' : showFlightDetail ? '' : 'request'"
        :style="{ 'background-color': selectedCard ? '#2d89d81a' : '' }">
        <v-toolbar density="compact">
            <v-toolbar-title>{{ flightItem.airlineNameEn }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items class="mr-5 mt-1">
                <div class="d-flex align-center justify-end">
                    <span class="status-icon"
                        :style="{ 'background-color': flightStatusOptions.find(o => o.value === flightItem.flightStatus)?.color }"></span>
                    <span v-if="isDelayed(flightItem.flightStatus)" class="mr-2"
                        :style="{ 'color': flightStatusOptions.find(o => o.value === flightItem.flightStatus)?.color }">
                        <span>{{ $t(`t-flight-status-delayed`) }}</span>
                    </span>
                    <span v-else
                        :style="{ 'color': flightStatusOptions.find(o => o.value === flightItem.flightStatus)?.color }">
                        <span v-if="flightStatusLabel()">{{ $t(`t-${flightStatusLabel()}`) }}</span>
                    </span>
                    <span v-if="isDelayed(flightItem.flightStatus)"
                        :style="{ 'color': flightStatusOptions.find(o => o.value === flightItem.flightStatus)?.color }">
                        {{ commonSv.timeString(flightItem.delayString) }}
                    </span>
                </div>
            </v-toolbar-items>
        </v-toolbar>
        <v-divider :style="{ 'border-color': selectedCard ? '#adc1cf' : '' }"></v-divider>
        <div class="flight-info">
            <div class="w-100 d-flex justify-space-between">
                <div class="route-info origin d-flex justify-start">
                    <div class="d-flex flex-column align-start justify-space-between pl-3 pr-4 py-3">
                        <div class="airport">{{ flightItem.depAirPortNameTh }}</div>
                        <div class="airport-code">{{ flightItem.depAirPortCodeIata }}</div>
                        <div class="time">{{
                            appDateTimeString(flightItem.schedDateTime?.departureLocalTimeString)?.split(' ')[1] }}
                        </div>
                        <div class="date">{{
                            appDateTimeString(flightItem.schedDateTime?.departureLocalTimeString)?.split(' ')[0] }}
                        </div>
                    </div>
                    <v-divider class="mt-8"></v-divider>
                </div>
                <div class="airplane d-flex flex-column justify-center align-center">
                    <img src="@/assets/images/flight/plane-mini.png" />
                    <div class="durations py-0 my-0">{{ commonSv.timeString(flightItem.durations) }}</div>
                    <v-chip class="mt-1" size="small" variant="outlined" color="#A3B3C6">
                        {{ flightItem.flightNo }}
                    </v-chip>
                </div>
                <div class="route-info destination d-flex justify-end">
                    <v-divider class="mt-8"></v-divider>
                    <div class="d-flex flex-column align-end justify-space-between pr-3 pl-4 py-3">
                        <div class="airport">{{ flightItem.arrAirPortNameTh }}</div>
                        <div class="airport-code">{{ flightItem.arrAirPortCodeIata }}</div>
                        <div class="time">{{
                            appDateTimeString(flightItem.schedDateTime?.arrivalLocalTimeString)?.split(' ')[1] }}</div>
                        <div class="date">{{
                            appDateTimeString(flightItem.schedDateTime?.arrivalLocalTimeString)?.split(' ')[0] }}</div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flight-details shadow" :style="{ 'background-color': selectedCard ? '#87b1d629' : '' }">
            <v-row v-if="showFlightDetail && canComplaint(flightItem)" class="mb-1 d-flex align-center">
                <v-col cols="6" sm="2" lg="2" class="pr-0">
                    <v-chip variant="flat"
                        :color="complainStatusOptions.find(o => o.value === flightItem.complaintStatus)?.color">
                        <span v-if="complaintStatusLabel()">{{ $t(`t-${complaintStatusLabel()}`) }}</span>
                    </v-chip>
                </v-col>
                <v-col cols="6" sm="9" lg="9">
                    <p class="remark">{{ flightItem.remark }}</p>
                </v-col>
                <v-col v-if="canComplaint(flightItem)" cols="12" sm="1" lg="1">
                    <v-btn class="complaint-btn-text" color="primary" variant="text" @click="displaySubTopic(true)">
                        {{ $t("t-complaint-btn") }}
                        <v-icon icon="mdi-chevron-right" />
                    </v-btn>
                    <v-btn class="complaint-btn-flat" color="primary" variant="flat" block
                        @click="displaySubTopic(true)">
                        {{ $t("t-complaint-btn") }}
                        <v-icon icon="mdi-chevron-right" />
                    </v-btn>
                </v-col>
            </v-row>
            <v-divider v-if="showFlightDetail && canComplaint(flightItem)"></v-divider>
            <div class="schedule-info" :class="showFlightDetail && canComplaint(flightItem) ? 'mt-3' : ''">
                <div class="d-flex">
                    <span class="label mr-3">{{ $t('t-scheduled-date') }}</span>
                    <span>{{ appDateTimeString(flightItem.schedDateTime?.departureLocalTimeString) }}</span>
                </div>
                <div class="splitter mx-2"></div>
                <div class="d-flex">
                    <span class="label mr-3">{{ $t('t-on-off-block') }}</span>
                    <span>{{ dateString(flightItem.onBlockDate) }}</span>
                </div>
                <div class="splitter mx-2"></div>
                <div class="d-flex">
                    <span class="label mr-3">{{ $t('t-target-off-block') }}</span>
                    <span>{{ dateString(flightItem.offBlockDate) }}</span>
                </div>
            </div>
        </div>
        <!-- <div v-if="showInputAirport && model.flight" class="flight-details pt-0" style="height: inherit;">
            <v-row>
                <v-col cols="12">
                    <div class="text-label">{{ $t('t-complaint-airline-occurred') }}</div>
                    <v-select v-model="model.flight.airport" class="hide-float-label" variant="outlined"
                        :label="$t('t-please-select')" hide-details="auto" :items="airportOptions" item-value="value"
                        item-title="label" density="comfortable" :readonly="viewMode"
                        :rules="[rules.required(`${$t('t-please-select')}${$t('t-complaint-airline-occurred')}`)]">
                    </v-select>
                </v-col>
            </v-row>
        </div> -->
    </v-card>
    <PreComplaint v-if="!viewMode" ref="preComplaint" :main-topic-id="1"></PreComplaint>
</template>
